import { useState } from "react";

import { Button, Stack, TextField, Typography } from "@mui/material";
import { isNil } from "lodash";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { useDebounce, useRoleContext, useSwrFetch } from "hooks";

import { COMPANY_SEARCH_PAGE_LIMIT } from "../../constants";
import { CompanyResultsTable } from "../CompanyResultsTable";
import { CompanySearchPagination } from "../CompanySearchPagination";

import type { CompanySearchResponse } from "components/CompanySetup/types";

interface Props {
  onCreateCompanySelected: () => void;
  onJoinRequestSent: () => void;
  previouslyRejectedByCompanyName?: string;
  previouslyRejectedCompanyIds: number[];
}

export function JoinCompany(props: Props) {
  const {
    onCreateCompanySelected,
    onJoinRequestSent,
    previouslyRejectedByCompanyName,
    previouslyRejectedCompanyIds,
  } = props;

  const { role, isLoading: isRoleLoading } = useRoleContext();

  const [searchValue, setSearchValue] = useState<string>("");
  const [offset, setOffset] = useState<number>(0);

  const debouncedSearchValue = useDebounce({ value: searchValue, delay: 500 });
  const isDebouncing = searchValue !== debouncedSearchValue;

  const searchPath = role?.type === "CARRIER" ? "carriers" : "shippers";
  const searchUrl =
    role != null && debouncedSearchValue !== ""
      ? `/companies/${searchPath}?${new URLSearchParams({
          name: debouncedSearchValue,
          limit: String(COMPANY_SEARCH_PAGE_LIMIT),
          offset: String(offset),
        }).toString()}`
      : null;

  const { data, loading, validating, error } =
    useSwrFetch<CompanySearchResponse>({
      url: searchUrl,
      errorMessage: "Failed to get companies.",
    });

  const companies = data?.results ?? [];
  const isLoading = isDebouncing || isRoleLoading || loading;

  const handleSearchValueChange = (value: string) => {
    setSearchValue(value);
    setOffset(0);
  };

  return (
    <Stack my="2rem" spacing={4} minWidth="200px">
      <Typography textAlign="center" variant="h4" fontWeight="bold">
        Join your company on Evotrux
      </Typography>
      {previouslyRejectedByCompanyName != null && (
        <Typography textAlign="center" color="text.secondary">
          Your request to join <b>{previouslyRejectedByCompanyName}</b> was not
          approved. You can select a company below to submit a new request.
        </Typography>
      )}
      <TextField
        value={searchValue}
        onChange={(e) => handleSearchValueChange(e.target.value)}
        placeholder="Search for your company..."
      />
      <LoadingWrappedNode loading={isLoading}>
        <Stack spacing={2}>
          <CompanyResultsTable
            searchBegun={searchValue.length > 0}
            companies={companies}
            onJoinRequestSent={onJoinRequestSent}
            previouslyRejectedCompanyIds={previouslyRejectedCompanyIds}
          />
          {data != null && data.count > 0 && (
            <CompanySearchPagination
              count={data.count}
              offset={offset}
              limit={data.limit}
              nextOffset={data.next_offset}
              previousOffset={data.previous_offset}
              onOffsetChange={setOffset}
              disabled={validating}
            />
          )}
        </Stack>
      </LoadingWrappedNode>
      {!isNil(error) && <ErrorTypography error={error} />}
      <Stack justifyContent="center" spacing={2} alignItems="center">
        <Typography variant="body1">
          <b>Is your company new to Evotrux?</b>
        </Typography>
        <Button variant="contained" onClick={onCreateCompanySelected}>
          Create Your Company Profile Now
        </Button>
      </Stack>
    </Stack>
  );
}
